import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Eye, FileText, Sparkles, Download, CheckCircle2,
  Users, Briefcase, Baby, Heart, Plane, PiggyBank,
  Leaf, Palette, Home as HomeIcon, ShoppingBag, Shield,
  Menu, X
} from 'lucide-react';
import { products, visibleProducts } from '../data/products';
import { demoPages } from '../data/demoPages';
import ProductCard from '../components/ProductCard';
import { getProductHeroImage } from '../data/productHeroImages';
import { ProductPageModal } from '../components/home/ProductPageModal';
import { AIChatAssistant } from '../components/home/AIChatAssistant';
import { PRO_PRICES, detectCurrency, formatPrice } from '../data/pricing';
import { ProductPurchaseDialog } from '../components/ProductPurchaseDialog';
import { CollectionsSection } from '../components/home/CollectionsSection';
import { FreeVsProComparison } from '../components/FreeVsProComparison';
import { CustomizationSection } from '../components/CustomizationSection';
import RecommendationSection from '../components/RecommendationSection';
import UserNavMenu from '../components/UserNavMenu';
import ThemeToggle from '../components/ThemeToggle';
import LumeraLogo from '../components/LumeraLogo';
import { PageMeta } from '../components/PageMeta';

/* ── Category & Template Data ──────────────────────── */
const macroCategories = [
  { name: 'Business', nameKey: 'home.categories.names.business', icon: Briefcase, subcats: ['Business'], template: 'Content Planner', color: '#D8D9F2' },
  { name: 'Baby & Family', nameKey: 'home.categories.names.babyFamily', icon: Baby, subcats: ['Baby'], template: 'Baby Milestone Book', color: '#FFF2C7' },
  { name: 'Wedding & Events', nameKey: 'home.categories.names.weddingEvents', icon: Heart, subcats: ['Wedding', 'Seasonal'], template: 'Wedding Planner', color: '#F6E6E2' },
  { name: 'Travel', nameKey: 'home.categories.names.travel', icon: Plane, subcats: ['Travel'], template: 'AI Travel Planner', color: '#CFEAEA' },
  { name: 'Finance', nameKey: 'home.categories.names.finance', icon: PiggyBank, subcats: ['Finance'], template: 'Budget Tracker', color: '#D6E3F7' },
  { name: 'Health & Wellness', nameKey: 'home.categories.names.healthWellness', icon: Leaf, subcats: ['Health', 'ADHD'], template: '12-Week Workout', color: '#E9D9F7' },
  { name: 'Creativity & Hobby', nameKey: 'home.categories.names.creativityHobby', icon: Palette, subcats: ['Hobby', 'Studies'], template: 'Self Care Journal', color: '#F6D9E0' },
  { name: 'Home & Lifestyle', nameKey: 'home.categories.names.homeLifestyle', icon: HomeIcon, subcats: ['Home', 'Food', 'Everyday', 'Relationships', 'Productivity', 'Pets'], template: 'Recipe Book', color: '#E3EAD6' },
];

const productTypeChips = [
  'Planner', 'Journal', 'Workbook', 'Ebook', 'Recipe Book', 'Memory Book',
  'Checklist', 'Course Guide', 'Budget Planner', 'Travel Planner',
  'Reading Journal', 'Pregnancy Journal', 'Pet Journal', 'Activity Book',
  'Fitness Program', 'Guide', 'Tracker', 'Carousel', 'Printable', 'Lead Magnet',
];

/* ── Home Page ─────────────────────────────────────── */
const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isPro, hasProductAccess } = useAuth();
  const [previewProduct, setPreviewProduct] = useState(null);
  const [purchaseProduct, setPurchaseProduct] = useState(null);
  const [featuredPage, setFeaturedPage] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const featuredIntervalRef = useRef(null);

  const heroProducts = useMemo(() => products.filter(p => demoPages[p.title] && !p.legacy).slice(0, 16), []);
  const featuredProduct = products[0];
  const featuredDemo = demoPages[featuredProduct.title];

  const curatedTemplates = useMemo(() =>
    macroCategories.map(cat => {
      const p = products.find(pr => pr.title === cat.template);
      return p && demoPages[p.title] ? { ...cat, product: p } : null;
    }).filter(Boolean), []);

  useEffect(() => {
    const pages = featuredDemo?.pages || [];
    if (pages.length <= 1) return;
    featuredIntervalRef.current = setInterval(() => {
      setFeaturedPage(p => (p + 1) % pages.length);
    }, 5000);
    return () => clearInterval(featuredIntervalRef.current);
  }, [featuredDemo]);

  const handleStartFree = () => navigate(isAuthenticated ? '/generator?type=daily-planner&prefill_topic=self+care' : '/register');
  const handleCreateProduct = (genType, genTopic, slug) => {
    setPreviewProduct(null);
    if (slug) {
      // Predefined product → use personalization wizard
      navigate(isAuthenticated ? `/personalize?product=${slug}` : '/register');
    } else {
      // Generic creation
      navigate(isAuthenticated ? `/generator?type=${genType}&prefill_topic=${encodeURIComponent(genTopic)}` : '/register');
    }
  };

  const handleBuyOrUse = (product, genType, genTopic) => {
    if (isPro || hasProductAccess(product.slug)) {
      handleCreateProduct(genType, genTopic, product.slug);
      return;
    }
    if (!isAuthenticated) {
      localStorage.setItem('lumera_pending_purchase', product.slug);
      navigate(`/login?redirect=${encodeURIComponent('/?buy=' + product.slug)}`);
      return;
    }
    setPurchaseProduct(product);
  };

  const handleTryPreview = (product, genType, genTopic) => {
    if (isPro || hasProductAccess(product.slug)) {
      handleCreateProduct(genType, genTopic, product.slug);
      return;
    }
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent('/personalize?product=' + product.slug)}`);
      return;
    }
    navigate(`/personalize?product=${product.slug}`);
  };

  useEffect(() => {
    if (isAuthenticated && !isPro) {
      const buySlug = searchParams.get('buy');
      const pendingSlug = buySlug || localStorage.getItem('lumera_pending_purchase');
      if (pendingSlug) {
        localStorage.removeItem('lumera_pending_purchase');
        const p = products.find(pr => pr.slug === pendingSlug);
        if (p && !hasProductAccess(pendingSlug)) {
          setPurchaseProduct(p);
        }
      }
    }
  }, [isAuthenticated, isPro, searchParams, hasProductAccess]);

  const getCategoryCount = (subcats) => visibleProducts.filter(p => subcats.includes(p.category)).length;

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#F7F2EC] dark:bg-background">
      <PageMeta
        title={t('home.meta.title')}
        description={t('home.meta.description')}
        url="/"
      />
      {/* ─── Nav ─── */}
      <nav data-testid="main-nav" className="fixed top-0 left-0 right-0 z-50 bg-[#F7F2EC]/85 dark:bg-background/90 backdrop-blur-md border-b border-[#DDD4C8]/50 dark:border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between h-[56px] md:h-[68px]">
          <LumeraLogo size="default" />
          <div className="hidden md:flex items-center gap-6 lg:gap-10 xl:gap-14">
            <button onClick={() => navigate('/templates')} className="nav-link-premium text-[11px] lg:text-[12px] font-medium text-[#8A7D72] hover:text-[#4A3525] transition-colors tracking-[0.12em] uppercase">Templates</button>
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="nav-link-premium text-[11px] lg:text-[12px] font-medium text-[#8A7D72] hover:text-[#4A3525] transition-colors tracking-[0.12em] uppercase">Features</button>
            <button onClick={() => navigate('/pricing')} className="nav-link-premium text-[11px] lg:text-[12px] font-medium text-[#8A7D72] hover:text-[#4A3525] transition-colors tracking-[0.12em] uppercase">Pricing</button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            {isAuthenticated ? (
              <UserNavMenu size={36} />
            ) : (<>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate('/login')} className="hidden sm:inline-flex text-[#8A7D72] hover:text-[#4A3525] font-medium text-sm tracking-[0.01em]">Log in</Button>
              <Button data-testid="nav-signup-btn" onClick={() => navigate('/register')} className="bg-[#8C7A66] text-[#FAF7F3] hover:bg-[#7A6A58] rounded-lg px-5 sm:px-7 py-2 sm:py-2.5 font-semibold text-xs sm:text-sm shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-[1px]">Start Free</Button>
            </>)}
            {/* Mobile hamburger menu */}
            <button data-testid="mobile-menu-toggle" onClick={() => setMobileMenuOpen(v => !v)} className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#EDE3D6] transition-colors">
              {mobileMenuOpen ? <X className="w-5 h-5 text-[#4A3525]" /> : <Menu className="w-5 h-5 text-[#4A3525]" />}
            </button>
          </div>
        </div>
        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden border-t border-[#DDD4C8]/50 bg-[#F7F2EC]/98 backdrop-blur-lg px-6 py-4 space-y-1 animate-fade-in-up">
            <button onClick={() => { navigate('/templates'); setMobileMenuOpen(false); }} className="w-full text-left py-2.5 text-sm font-medium text-[#4A3525] hover:text-[#B49A88] transition-colors">Templates</button>
            <button onClick={() => { document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} className="w-full text-left py-2.5 text-sm font-medium text-[#4A3525] hover:text-[#B49A88] transition-colors">Features</button>
            <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="w-full text-left py-2.5 text-sm font-medium text-[#4A3525] hover:text-[#B49A88] transition-colors">Pricing</button>
            {!isAuthenticated && (
              <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="w-full text-left py-2.5 text-sm font-medium text-[#4A3525] hover:text-[#B49A88] transition-colors">Log in</button>
            )}
          </div>
        )}
      </nav>

      {/* ─── Hero ─── */}
      <section data-testid="hero-section" className="pt-24 pb-0 md:pt-32 md:pb-0 relative overflow-hidden bg-[#F7F2EC]">
        {/* Botanical shadow overlay — smooth fade with no visible artifacts */}
        <div className="absolute inset-0 pointer-events-none z-0 bg-[#F7F2EC]" style={{ mixBlendMode: 'multiply', opacity: 0.30 }}>
          <img src="https://static.prod-images.emergentagent.com/jobs/6011386d-adfc-4415-9257-23eae86765a4/images/b67b1d09036251c96fb980c18ccecd4ae3c4da9e60de654eaf80582f937d1113.jpeg" alt="" className="w-full h-full object-cover" style={{ filter: 'blur(14px)', WebkitMaskImage: 'linear-gradient(to right, black 20%, black 35%, transparent 55%), linear-gradient(to bottom, black 40%, transparent 80%)', WebkitMaskComposite: 'destination-in', maskImage: 'linear-gradient(to right, black 20%, black 35%, transparent 55%), linear-gradient(to bottom, black 40%, transparent 80%)', maskComposite: 'intersect' }} />
        </div>
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-semibold tracking-tight text-[#4A3525] leading-[1.08] mb-5 animate-fade-in-up">
              From idea to income.<br /><span className="italic text-[#C4A98D]">In 60 seconds.</span>
            </h1>
            <p className="text-base md:text-lg text-[#8A7D72] leading-relaxed max-w-lg mx-auto mb-9 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Browse real templates. Customize instantly. Start selling on Etsy, Pinterest & Shopify.
            </p>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <Button data-testid="hero-cta-btn" onClick={handleStartFree} className="btn-premium bg-[#EDE3D6] text-[#4A3525] hover:bg-[#E4D8C8] hover:-translate-y-1 transition-all duration-500 font-semibold px-14 py-7 rounded-full text-base shadow-sm hover:shadow-md">
                Start creating free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-3 sm:gap-5 mt-7 flex-wrap animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <span className="text-sm text-[#8A7D72] font-medium tracking-[0.01em]">{visibleProducts.length}+ premium templates</span>
              <div className="w-px h-4 bg-[#DDD4C8]" />
              <span className="text-sm text-[#8A7D72] font-medium tracking-[0.01em]">Built for Etsy, Pinterest & Shopify</span>
            </div>
          </div>
          {/* Scrolling hero carousel */}
          <div className="relative mt-12 md:mt-16 overflow-hidden" style={{ height: 'clamp(220px, 32vw, 340px)' }}>
            <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10" style={{ background: 'linear-gradient(to right, #F7F2EC, transparent)' }} />
            <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10" style={{ background: 'linear-gradient(to left, #F7F2EC, transparent)' }} />
            <div className="hero-scroll-track flex items-end gap-4 md:gap-5" style={{ animation: `heroScroll ${heroProducts.length * 3}s linear infinite`, width: 'max-content' }}>
              {[...heroProducts, ...heroProducts].map((p, i) => (
                <div key={`hero-${i}`} className="flex-shrink-0 w-[120px] sm:w-[150px] md:w-[180px] transition-transform duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer" onClick={() => setPreviewProduct(p)}>
                  <div className="bg-white rounded-lg md:rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.05)] overflow-hidden aspect-[3/4] border border-[#DDD4C8]/60">
                    {demoPages[p.title]?.pages[0]({ accent: p.colors.accent })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── AI Chat Assistant ─── */}
      <AIChatAssistant onPreviewProduct={setPreviewProduct} />

      {/* ─── Start med en kategori ─── */}
      <section data-testid="category-section" className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#4A3525] tracking-tight mb-2">{t('home.categories.heading')}</h2>
            <p className="text-sm text-[#A89B8E]">{t('home.categories.subheading')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {macroCategories.map(cat => {
              const count = getCategoryCount(cat.subcats);
              return (
                <button key={cat.name} data-testid={`macro-cat-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => document.getElementById('popular-templates')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group relative flex flex-col items-center gap-3 p-6 md:p-7 rounded-xl border border-[#DDD4C8]/60 bg-white hover:border-transparent transition-all duration-400 hover:shadow-[0_12px_32px_-8px_rgba(41,38,36,0.08)] hover:-translate-y-1"
                  style={{ '--cat-color': cat.color }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-400" style={{ backgroundColor: cat.color }}>
                    <cat.icon className="w-5 h-5 text-[#4A3525]/70" strokeWidth={1.5} />
                  </div>
                  <span className="text-sm font-semibold text-[#4A3525]">{t(cat.nameKey)}</span>
                  <span className="text-[11px] text-[#A89B8E]">{t('home.categories.productCount', { count })}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Popular Templates ─── */}
      <section id="popular-templates" data-testid="popular-templates" className="py-14 md:py-20 bg-white dark:bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#C4A98D] mb-3">{t('home.popular.eyebrow')}</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#4A3525] tracking-tight mb-2">{t('home.popular.heading')}</h2>
            <p className="text-sm text-[#8A7D72] max-w-md mx-auto">{t('home.popular.subheading')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-7">
            {curatedTemplates.map(({ name, product, subcats }) => {
              const demo = demoPages[product.title];
              const shouldShowBuy = !isPro && !hasProductAccess(product.slug);
              return (
                <ProductCard
                  key={product.title}
                  product={product}
                  category={product.category}
                  categoryLabel={name}
                  demoPages={demoPages}
                  onPreview={() => setPreviewProduct(product)}
                  onAction={() => handleTryPreview(product, demo?.genType, demo?.genTopic)}
                  onBuy={() => handleBuyOrUse(product, demo?.genType, demo?.genTopic)}
                  showBuy={shouldShowBuy}
                />
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Button data-testid="browse-all-btn" onClick={() => navigate('/templates')} variant="outline" className="rounded-lg px-8 py-5 font-semibold border-[#DDD4C8] text-[#8A7D72] hover:bg-[#EDE3D6] text-sm tracking-[0.01em]">
              {t('home.popular.browseAll', { count: visibleProducts.length })} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ─── Collections / Bundles ─── */}
      <CollectionsSection />

      {/* ─── Smart Recommendations ─── */}
      {isAuthenticated && (
        <section className="py-14 md:py-20 bg-stone-50/30">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <RecommendationSection
              placement="home"
              currency={detectCurrency() === 'nok' ? 'nok' : 'usd'}
              title="Recommended for you"
              limit={8}
            />
          </div>
        </section>
      )}

      {/* ─── Customization Section ─── */}
      <CustomizationSection className="bg-white" />

      {/* ─── Free vs Pro ─── */}
      <section className="py-14 md:py-20 bg-stone-50/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <FreeVsProComparison />
        </div>
      </section>

      {/* ─── Featured Preview ─── */}
      <section data-testid="featured-preview" className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="relative bg-white rounded-xl shadow-[0_8px_60px_rgba(0,0,0,0.06)] border border-[#DDD4C8]/60 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="relative p-10 md:p-14 flex items-center justify-center cursor-pointer group overflow-hidden" onClick={() => setPreviewProduct(featuredProduct)}>
                {/* Lifestyle hero background */}
                {(() => {
                  const fImg = getProductHeroImage(featuredProduct.slug, 'hero');
                  return fImg ? (
                    <>
                      <img src={fImg.url} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: fImg.focal || 'center' }} />
                      <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/40 to-white/10" />
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0" style={{ background: '#FAF5F0' }} />
                      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />
                    </>
                  );
                })()}
                <div className="absolute top-5 left-5 text-[9px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-md bg-white/90 backdrop-blur-sm text-[#4A3525] border border-[#DDD4C8] shadow-sm z-10">Best sor</div>
                <div className="relative w-full max-w-[260px]">
                  <div className="absolute inset-0 bg-white/50 rounded-xl shadow-sm" style={{ transform: 'rotate(4deg) translateX(10px) translateY(-6px) scale(0.92)' }} />
                  <div className="absolute inset-0 bg-white/70 rounded-xl shadow-md" style={{ transform: 'rotate(2deg) translateX(5px) translateY(-3px) scale(0.96)' }} />
                  <div className="relative bg-stone-800 rounded-xl p-2.5 shadow-2xl group-hover:shadow-[0_25px_60px_rgba(0,0,0,0.2)] transition-shadow duration-700">
                    <div className="bg-white rounded-xl overflow-hidden aspect-[3/4] relative">
                      {featuredDemo.pages.map((PageComponent, i) => (
                        <div key={i} className="absolute inset-0 transition-transform duration-700 ease-out" style={{ transform: `translateX(${(i - featuredPage) * 100}%)` }}>
                          {PageComponent({ accent: featuredProduct.colors.accent })}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-center gap-1.5 mt-4">
                    {featuredDemo.pages.map((_, i) => (
                      <button key={i} onClick={(e) => { e.stopPropagation(); clearInterval(featuredIntervalRef.current); setFeaturedPage(i); featuredIntervalRef.current = setInterval(() => setFeaturedPage(p => (p + 1) % featuredDemo.pages.length), 5000); }}
                        className={`rounded-full transition-all duration-300 ${i === featuredPage ? 'w-6 h-2 bg-stone-800 shadow-sm' : 'w-2 h-2 bg-stone-300 hover:bg-stone-400'}`} />
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="bg-white/95 backdrop-blur-sm rounded-full px-5 py-2.5 shadow-xl flex items-center gap-2"><Eye className="w-4 h-4 text-stone-700" /><span className="text-xs font-sans font-bold text-stone-700">{t('common.preview')}</span></div>
                  </div>
                </div>
              </div>
              <div className="p-10 md:p-14 flex flex-col justify-center">
                <p className="text-[10px] text-[#A89B8E] font-bold uppercase tracking-[0.2em] mb-4">Featured Product</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-[#4A3525] mb-3 tracking-tight leading-tight">{featuredProduct.title}</h2>
                <p className="text-base text-[#8A7D72] mb-1">{featuredProduct.sub}</p>
                <p className="text-sm text-[#8A7D72]/70 mb-4">{featuredDemo.includes}</p>
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex gap-3">{[
                    { icon: Download, text: 'Instant download' },
                    { icon: Palette, text: 'Canva editable' },
                  ].map(b => (
                    <div key={b.text} className="flex items-center gap-1.5 text-xs text-[#8A7D72]">
                      <b.icon className="w-3.5 h-3.5 text-[#A89B8E]" />{b.text}
                    </div>
                  ))}</div>
                </div>
                <div className="space-y-2 mb-6">
                  {featuredDemo.features.slice(0, 4).map(f => (<div key={f} className="flex items-center gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#A89B8E] flex-shrink-0" /><span className="text-sm text-[#8A7D72]">{f}</span></div>))}
                </div>
                <p className="text-xs text-[#8A7D72] italic mb-6">Create and sell on Etsy, Pinterest & Shopify</p>
                <div className="flex gap-3">
                  {!isPro && !hasProductAccess(featuredProduct.slug) ? (
                    <>
                      <Button data-testid="featured-try-btn" onClick={() => handleTryPreview(featuredProduct, featuredDemo.genType, featuredDemo.genTopic)} className="flex-1 bg-[#4A3525] text-white hover:bg-[#3D2B1F] rounded-lg px-6 py-6 font-semibold text-[13px] tracking-[0.02em] shadow-lg hover:shadow-xl hover:-translate-y-[1px] transition-all">Try free <ArrowRight className="ml-2 w-4 h-4" /></Button>
                      <Button data-testid="featured-buy-btn" variant="outline" onClick={() => handleBuyOrUse(featuredProduct, featuredDemo.genType, featuredDemo.genTopic)} className="rounded-lg px-5 py-6 font-semibold border-[#DDD4C8] text-[#8A7D72] hover:bg-[#EDE3D6]"><ShoppingBag className="w-4 h-4 mr-1.5" /> {featuredProduct.price}</Button>
                    </>
                  ) : (
                    <Button data-testid="featured-create-btn" onClick={() => handleBuyOrUse(featuredProduct, featuredDemo.genType, featuredDemo.genTopic)} className="flex-1 bg-[#4A3525] text-white hover:bg-[#3D2B1F] rounded-lg px-6 py-6 font-semibold text-[13px] tracking-[0.02em] shadow-lg hover:shadow-xl hover:-translate-y-[1px] transition-all">Create this product <ArrowRight className="ml-2 w-4 h-4" /></Button>
                  )}
                  <Button data-testid="featured-preview-btn" variant="outline" onClick={() => setPreviewProduct(featuredProduct)} className="rounded-lg px-5 py-6 font-semibold border-[#DDD4C8] text-[#8A7D72] hover:bg-[#EDE3D6]"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── What can Lumera create? ─── */}
      <section data-testid="what-we-make" className="py-14 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#4A3525] tracking-tight mb-2">What can Lumera create?</h2>
            <p className="text-sm text-[#8A7D72]">Over {visibleProducts.length}+ unique product types &mdash; all AI-generated</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5 md:gap-3">
            {productTypeChips.map(chip => (
              <span key={chip} data-testid={`chip-${chip.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm font-medium px-4 py-2 rounded-lg bg-white text-[#8A7D72] border border-[#DDD4C8]/60 hover:border-[#A89B8E]/30 hover:text-[#4A3525] transition-all duration-300 cursor-default">
                {chip}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How It Works — Buyer Journey ─── */}
      <section id="features" className="py-20 md:py-28" data-testid="how-lumera-works">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#4A3525] tracking-tight">How Lumera works</h2>
            <p className="text-sm text-[#8A7D72] mt-3 max-w-md mx-auto">Find something you love, make it yours, and keep it forever</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {[
              { step: '01', title: 'Choose', desc: 'Browse planners, trackers, guides and creative tools.', icon: Eye },
              { step: '02', title: 'Personalize', desc: 'Answer a few questions. We build it around your life.', icon: Sparkles },
              { step: '03', title: 'Preview', desc: 'See everything before you commit. Try it for free.', icon: FileText },
              { step: '04', title: 'Unlock', desc: 'Buy just this product — or get Pro for full access.', icon: ShoppingBag },
              { step: '05', title: 'Create & Export', desc: 'Finish your product and download your clean result.', icon: Download },
            ].map(item => (
              <div key={item.step} className="text-center group" data-testid={`how-step-${item.step}`}>
                <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all duration-400 border border-[#DDD4C8]/40">
                  <item.icon className="w-5 h-5 text-[#C4A98D]" strokeWidth={1.5} />
                </div>
                <span className="text-[9px] font-bold text-[#A89B8E] tracking-[0.3em] uppercase">{item.step}</span>
                <h3 className="text-sm md:text-base font-semibold text-[#4A3525] mt-1.5 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-[#8A7D72] text-xs leading-relaxed max-w-[160px] mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── For Creators ─── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#4A3525] tracking-tight">Also a creator?</h2>
            <p className="text-sm text-[#8A7D72] mt-3 max-w-md mx-auto">Turn your ideas into sellable digital products in under 60 seconds</p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { step: '01', title: 'Pick a template', desc: `Choose from ${visibleProducts.length}+ premium templates — planners, guides, trackers, journals.`, icon: FileText },
              { step: '02', title: 'Describe your idea', desc: 'Enter your niche and audience. AI generates all the content and designs it.', icon: Sparkles },
              { step: '03', title: 'Export & sell', desc: 'Download as print-ready PDF or PNG. Start selling on Etsy, Pinterest, Shopify.', icon: Download }
            ].map(item => (
              <div key={item.step} className="text-center group">
                <div className="w-14 h-14 rounded-lg bg-[#F7F2EC] flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-all duration-400 border border-[#DDD4C8]/40">
                  <item.icon className="w-6 h-6 text-[#A89B8E]" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] font-bold text-[#A89B8E] tracking-[0.3em] uppercase">{item.step}</span>
                <h3 className="text-lg font-semibold text-[#4A3525] mt-2 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-[#8A7D72] text-sm leading-relaxed max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Creator value props ─── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#4A3525] tracking-tight">Built for creators who sell</h2>
            <p className="text-sm text-[#8A7D72] mt-3">From idea to income &mdash; here&apos;s what you can do with Lumera</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Launch fast', text: 'Go from blank page to a sell-ready digital product in under 60 seconds. No design skills needed.', metric: '60s to create' },
              { title: 'Save on design', text: 'Skip the freelancer. AI generates professional layouts, content, and designs instantly.', metric: `${visibleProducts.length}+ templates` },
              { title: 'Sell everywhere', text: 'Export to PDF or PNG. List on Etsy, promote on Pinterest, or sell through Shopify.', metric: '3 platforms' }
            ].map(t => (
              <div key={t.title} className="bg-white p-7 rounded-xl border border-[#DDD4C8]/60 shadow-[0_2px_16px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-500">
                <p className="text-[#8A7D72] text-sm leading-relaxed mb-5">{t.text}</p>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-[#4A3525] text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</p>
                  <span className="text-[10px] font-semibold text-[#C4A98D] bg-[#C4A98D]/10 px-2.5 py-1 rounded-full">{t.metric}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#4A3525] tracking-tight mb-3">Start free. <span className="italic text-[#A89B8E]">Scale when ready.</span></h2>
            <p className="text-base text-[#8A7D72]">No hidden fees. Cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="bg-white rounded-xl border border-[#DDD4C8]/80 p-8">
              <h3 className="font-semibold text-[#4A3525] text-lg mb-1">Free</h3>
              <p className="font-bold text-4xl text-[#4A3525] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>$0<span className="text-base font-normal text-[#8A7D72]" style={{ fontFamily: "'Inter', sans-serif" }}>/month</span></p>
              <div className="space-y-3 mb-7">{['3 generations / month', 'All product types', 'PNG export with branding'].map(f => (<div key={f} className="flex items-center gap-2.5"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" /><span className="text-sm text-[#8A7D72]">{f}</span></div>))}</div>
              <Button data-testid="pricing-free-btn" onClick={() => navigate(isAuthenticated ? '/generator' : '/register')} variant="outline" className="w-full rounded-lg py-6 font-semibold border-[#DDD4C8] text-sm">Get started free</Button>
            </div>
            <div className="bg-[#C8BAA9] rounded-xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-[#8C7A66] text-white text-[9px] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full">Most Popular</div>
              <h3 className="font-semibold text-[#4A3525] text-lg mb-1">Pro</h3>
              <p className="font-bold text-4xl text-[#4A3525] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>${PRO_PRICES.monthly.usd}<span className="text-base font-normal text-[#6B5C4E]" style={{ fontFamily: "'Inter', sans-serif" }}>/month</span></p>
              <div className="space-y-3 mb-7">{['Unlimited generations', `All ${visibleProducts.length}+ templates`, 'PDF + PNG export', 'Pinterest Pin Creator', 'Commercial License included'].map(f => (<div key={f} className="flex items-center gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#8C7A66] flex-shrink-0" /><span className="text-sm text-[#5C4D3F]">{f}</span></div>))}</div>
              <Button data-testid="pricing-pro-btn" onClick={() => navigate('/pricing')} className="w-full bg-[#4A3525] text-white hover:bg-[#3D2B1F] rounded-lg py-6 font-semibold shadow-lg text-sm">Upgrade to Pro <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="bg-[#C8BAA9] rounded-xl p-12 md:p-20 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-[#4A3525] tracking-tight mb-5">Your first product is<br /><span className="italic text-[#FAF7F3]">60 seconds</span> away</h2>
              <p className="text-base text-[#6B5C4E] max-w-md mx-auto mb-10">Start creating and selling digital products — no design skills required.</p>
              <Button data-testid="cta-start-btn" onClick={handleStartFree} className="btn-premium bg-[#4A3525] text-white hover:bg-[#3D2B1F] hover:-translate-y-1 transition-all duration-500 font-semibold px-12 py-7 rounded-lg text-base shadow-lg hover:shadow-xl">
                Create your first product now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Sticky Mobile CTA ─── */}
      {!previewProduct && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 dark:bg-card/95 backdrop-blur-xl border-t border-[#DDD4C8] dark:border-border px-3 py-2">
          <Button data-testid="sticky-mobile-cta" onClick={handleStartFree} className="w-full bg-[#4A3525] text-white hover:bg-[#3D2B1F] font-semibold py-3.5 rounded-lg text-sm shadow-lg">Start free <ArrowRight className="ml-1.5 w-4 h-4" /></Button>
        </div>
      )}

      {/* ─── Footer ─── */}
      <footer className="py-12 bg-white dark:bg-card border-t border-[#DDD4C8]/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/lumera-logo.png" alt="Lumera" className="w-7 h-7 rounded-full" />
                <span className="font-semibold text-[#4A3525] text-sm tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Create by Lumera</span>
              </div>
              <p className="text-xs text-[#8A7D72] leading-relaxed">Create and sell digital products in minutes.</p>
            </div>
            <div><p className="font-semibold text-[#4A3525] text-xs mb-3 tracking-[0.12em] uppercase">Product</p><div className="space-y-2"><button onClick={() => navigate('/templates')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Templates</button><button onClick={() => navigate('/generator')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Generator</button><button onClick={() => navigate('/pricing')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Pricing</button></div></div>
            <div><p className="font-semibold text-[#4A3525] text-xs mb-3 tracking-[0.12em] uppercase">Account</p><div className="space-y-2"><button onClick={() => navigate('/dashboard')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Dashboard</button><button onClick={() => navigate('/profile')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Profile</button><button onClick={() => navigate('/contact')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Support</button></div></div>
            <div><p className="font-semibold text-[#4A3525] text-xs mb-3 tracking-[0.12em] uppercase">Legal</p><div className="space-y-2"><button onClick={() => navigate('/privacy')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Privacy Policy</button><button onClick={() => navigate('/terms')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Terms of Service</button><button onClick={() => navigate('/refund-policy')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Refund Policy</button><button onClick={() => navigate('/cookies')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Cookie Policy</button><button onClick={() => navigate('/subscription-terms')} className="block text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Subscription Terms</button></div></div>
          </div>
          <div className="border-t border-[#DDD4C8]/50 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-[#8A7D72]">&copy; {new Date().getFullYear()} Create by Lumera. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/privacy')} className="text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Privacy</button>
              <button onClick={() => navigate('/terms')} className="text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Terms</button>
              <button onClick={() => navigate('/contact')} className="text-xs text-[#8A7D72] hover:text-[#4A3525] transition-colors">Contact</button>
            </div>
          </div>
        </div>
      </footer>

      {/* ─── Product Page Modal ─── */}
      {previewProduct && (
        <ProductPageModal
          product={previewProduct}
          onClose={() => setPreviewProduct(null)}
          onCreateProduct={handleCreateProduct}
          onNextProduct={(p) => setPreviewProduct(p)}
        />
      )}

      {/* ─── Product Purchase Dialog ─── */}
      <ProductPurchaseDialog
        product={purchaseProduct}
        open={!!purchaseProduct}
        onOpenChange={(open) => { if (!open) setPurchaseProduct(null); }}
        initialLicenseType={null}
      />
    </div>
  );
};

export default Home;
